// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { ActionIcon, Autocomplete, Flex, Group, NumberInput, Select, Stack, Tooltip } from '@mantine/core';
import { FC, useContext, useMemo, useState } from 'react';
import { IconTag, IconTrashX } from '@tabler/icons';
import { useLocation } from 'react-router-dom';
import { i18n } from '@lingui/core';
import { DiscountType, PositionDto, TaxType } from '../../../Service/DocumentT';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { DataS } from '../../../Service/DataS';
import { TestS } from '../../../TestS';
import BookitupSegmentedControl from '../../../Atoms/BookitupSegmentedControl';
import { FormatS } from '../../../Service/FormatS';
import { EventRichInput, RichInput } from '../../../Organismns/tiptap/TiptapEditorInput';
import ResourcePositionAssignment from '../../../Organismns/Settings/Documents/Positions/ResourcePositionAssignment';
import { ProductsContext } from '../../../Service/Context/proxy/ProductsContext';
import DocumentBubbleMenu from '../../../Organismns/tiptap/toolbars/BubbleMenu';
import { ProductPositionPreviewActions } from './PositionActions';
import { ProductPositionPreview } from './PositionPreview';
import PositionAmountInput from './PositionAmountInput';
import { openEditPositionCategoryModal } from './AddProductModal';
import { ThemeText } from '../../../Atoms/ThemeText';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import { LicenceS } from '../../../Service/LicenceS';
import { TiptapS } from '../../../Service/TiptapS';
import { DocumentService } from '../../../Service/helper/DocumentService';
import { ContactContext } from '../../../Service/Context/ContactContext';

interface Props {
  position: PositionDto;
  taxType: TaxType;
  previewVersion?: boolean;
}

const ProductPosition: FC<Props> = ({ position, taxType, previewVersion = false }) => {
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const { editProduct, handleChange, categories, deleteProduct, currencySymbol } = useContext(ProductsContext);
  const { profile } = useContext(ProfileContext);
  const [resourcePresent, setResourcePresent] = useState(false);
  const { contact } = useContext<any>(ContactContext);
  const { country } = contact ?? {};
  const taxRatesData = useMemo(() => DataS.getTaxRatesByCountry(country, position.tax), [country]);
  const { pathname } = useLocation();
  const isTemplateScope = pathname.includes('settings');
  const {
    discount,
    id: positionId,
    label,
    subtext,
    price,
    tax,
    priceUnit,
    unit,
    amount,
    positionType,
    netPriceUnit,
    netPrice,
  } = position;
  const { value: discountValue, type: discountType } = discount ?? { amount: 0, type: 'PERCENT' };
  const isSmallScaleBusiness = profile && profile.smallScaleBusiness;

  if (previewVersion) {
    return (
      <Flex
        sx={(theme) => ({
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[0],
          border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : 'none'}`,
          padding: 10,
          borderRadius: 6,
        })}
      >
        <div style={{ flexGrow: 1 }}>
          <ProductPositionPreview favoritePosition position={position} />
        </div>
        <div>
          <ProductPositionPreviewActions position={position} />
        </div>
      </Flex>
    );
  }

  return (
    <Group noWrap spacing={5}>
      <Stack
        p="xs"
        spacing="xs"
        sx={(theme) => ({
          flexGrow: 1,
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
          borderRadius: 10,
        })}
      >
        <Group spacing="xs" pos="relative">
          <div style={{ width: position.category ? 310 : 320 }}>
            <RichInput
              value={label}
              patchField={(val) => editProduct(positionId, { label: val })}
              menu={<DocumentBubbleMenu />}
            />
          </div>
          {positionType !== 'FREE_TEXT' && (
            <>
              <PositionAmountInput position={position} onChange={(patch) => handleChange(positionId, patch)} />
              <Autocomplete
                defaultValue={unit ?? ''}
                data={DataS.positionUnitSuggestions}
                onBlur={(e) => editProduct(positionId, { unit: e.currentTarget.value })}
                sx={{ width: 60 }}
              />
              <NumberInput
                hideControls
                removeTrailingZeros
                decimalSeparator=","
                precision={2}
                value={taxType === 'gross' ? priceUnit : netPriceUnit}
                sx={{ width: 70 }}
                onBlur={(e) => {
                  const tmp = FormatS.formatAsCurrency(e.currentTarget.value);
                  editProduct(positionId, {
                    priceUnit: taxType === 'gross' ? tmp : DocumentService.calculateGrossPrice(tmp, tax, true),
                  });
                }}
                data-testid={TestS.Documents.Edit.Position.DISCOUNT_VALUE}
              />
              {!isSmallScaleBusiness && (
                <Select
                  value={tax.toString()}
                  data={taxRatesData}
                  sx={{ width: 55 }}
                  onChange={(val) => editProduct(positionId, { tax: Number(val) })}
                  // eslint-disable-next-line react/jsx-no-useless-fragment
                  rightSection={<></>}
                  rightSectionWidth={0}
                />
              )}
              <>
                <NumberInput
                  hideControls
                  min={0}
                  decimalSeparator=","
                  precision={2}
                  removeTrailingZeros
                  value={discountValue}
                  sx={{ width: 60 }}
                  onBlur={(e) =>
                    editProduct(positionId, { discountValue: FormatS.formatAsCurrency(e.currentTarget.value) })
                  }
                  data-testid={TestS.Documents.Edit.Position.DISCOUNT_VALUE}
                />
                <BookitupSegmentedControl
                  value={discountType}
                  data={DataS.getDiscountUnitOptions(currencySymbol)}
                  onChange={(val: DiscountType) => val && editProduct(positionId, { discountType: val })}
                  sx={{ marginLeft: -10 }}
                  testId={TestS.Documents.Edit.Position.DISCOUNT_TYPE}
                />
              </>
              <ThemeText sx={(theme) => ({ width: 115, color: theme.colorScheme === 'dark' ? 'white' : 'black' })}>
                {FormatS.formatPriceWithCountry(country, taxType === 'net' ? netPrice : price)}
              </ThemeText>
            </>
          )}
        </Group>
        {!isTemplateScope && (
          <EventRichInput
            value={subtext}
            menu={<DocumentBubbleMenu />}
            patchField={(val) => editProduct(positionId, { subtext: TiptapS.convertParagraphsToBreaks(val) })}
          />
        )}
        {isTemplateScope && (
          <RichInput
            value={subtext}
            menu={<DocumentBubbleMenu />}
            patchField={(val) => editProduct(positionId, { subtext: TiptapS.convertParagraphsToBreaks(val) })}
          />
        )}
        <Group>
          {positionType !== 'FREE_TEXT' && (
            <ResourcePositionAssignment
              favoritePosition
              position={position}
              amount={amount}
              setResourcePresent={setResourcePresent}
            />
          )}
          {positionType === 'FREE_TEXT' && <div />}
        </Group>
      </Stack>
      <Stack justify="center">
        <Tooltip openDelay={300} position="left" transition="slide-left" label="Gruppe">
          <ActionIcon size="xs">
            <IconTag
              onClick={() =>
                executeWithLicenceCheck(LicenceS.Restrictions.PRODUCTS_CATEGORIES, () => {
                  openEditPositionCategoryModal({
                    product: position,
                    categories,
                    onConfirm: (value) => handleChange(positionId, { categoryId: value }),
                  });
                })
              }
            />
          </ActionIcon>
        </Tooltip>
        <Tooltip openDelay={300} position="left" transition="slide-left" label={i18n._('actions.delete')}>
          <ActionIcon size="xs">
            <IconTrashX onClick={() => deleteProduct(positionId)} />
          </ActionIcon>
        </Tooltip>
      </Stack>
    </Group>
  );
};

export default ProductPosition;
